import { setApplicationInsights } from "@/core/utilities/application-insights/application-insights";
import type { App, Plugin } from "vue";
import type { Router } from "vue-router";

export interface IApplicationInsightsPluginOptions {
  instrumentationKey?: string;
  router?: Router;
}

// Manually shaped to match vue3-application-insights' exports — its own type declarations
// aren't reliably resolved through a dynamic `import()`, which trips `no-unsafe-assignment`.
interface IAppInsightsPluginModule {
  AppInsightsPlugin: {
    install: (
      app: App,
      options: {
        appInsightsConfig?: { config?: { instrumentationKey?: string } };
        router?: Router;
        trackAppErrors?: boolean;
        trackInitialPageView?: boolean;
        onLoaded?: typeof setApplicationInsights;
      },
    ) => void;
  };
}

const OPTANON_CONSENT_COOKIE_NAME = "OptanonConsent=";
const PERFORMANCE_CONSENT_GROUP = "C0002:1";

/** True once the OneTrust `OptanonConsent` cookie includes the accepted performance/analytics group state (`C0002:1`). */
const hasPerformanceConsent = (): boolean => {
  const cookieValue = document.cookie.split("; ").find((row) => row.startsWith(OPTANON_CONSENT_COOKIE_NAME));
  const rawCookieValue = cookieValue?.slice(OPTANON_CONSENT_COOKIE_NAME.length);

  if (!rawCookieValue) {
    return false;
  }

  const consentGroups = new URLSearchParams(rawCookieValue).get("groups");

  return consentGroups?.split(",").includes(PERFORMANCE_CONSENT_GROUP) ?? false;
};

const loadAppInsights = async (app: App, pluginOptions: IApplicationInsightsPluginOptions): Promise<void> => {
  const { AppInsightsPlugin } = (await import("vue3-application-insights")) as unknown as IAppInsightsPluginModule;

  app.use(AppInsightsPlugin, {
    appInsightsConfig: {
      config: {
        instrumentationKey: pluginOptions.instrumentationKey,
      },
    },
    router: pluginOptions.router,
    trackAppErrors: true,
    trackInitialPageView: true,
    onLoaded: setApplicationInsights,
  });
};

export const applicationInsightsPlugin = {
  install: (app: App, pluginOptions?: IApplicationInsightsPluginOptions) => {
    const instrumentationKey = pluginOptions?.instrumentationKey;

    if (!instrumentationKey) {
      return;
    }

    let loaded = false;
    const tryLoad = () => {
      if (loaded || !hasPerformanceConsent()) {
        return;
      }
      loaded = true;
      void loadAppInsights(app, pluginOptions);
    };

    // Consent may already be granted from a previous session.
    tryLoad();
    if (loaded) {
      return;
    }

    // Otherwise wait for OneTrust to signal a consent change (chain onto any existing wrapper,
    // e.g. the one registered by the OneTrust plugin itself).
    const originalWrapper = window.OptanonWrapper;
    window.OptanonWrapper = () => {
      originalWrapper?.();
      tryLoad();
    };
  },
} satisfies Plugin<[IApplicationInsightsPluginOptions?]>;
